import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { stockMovementService } from "~/services/purchasing";
import type {
  IngredientType,
  StockAdjustmentFormData,
} from "~/types/purchasing";

const MOVEMENTS_KEY = "stock_movements";
const OIL_TYPES_KEY = "oil_types";
const RAW_MATERIALS_KEY = "raw_materials";

/**
 * Fetch stock movements for a specific ingredient
 */
export function useStockMovements(
  ingredientType: IngredientType | undefined,
  ingredientId: string | undefined
) {
  return useQuery({
    queryKey: [MOVEMENTS_KEY, ingredientType, ingredientId],
    queryFn: () =>
      stockMovementService.getMovements(ingredientType!, ingredientId!),
    enabled: !!ingredientType && !!ingredientId,
  });
}

/**
 * Fetch stock movements for a specific oil type
 */
export function useOilMovements(oilTypeId: string | undefined) {
  return useQuery({
    queryKey: [MOVEMENTS_KEY, "oil", oilTypeId],
    queryFn: () => stockMovementService.getOilMovements(oilTypeId!),
    enabled: !!oilTypeId,
  });
}

/**
 * Fetch stock movements for a specific raw material
 */
export function useRawMaterialMovements(rawMaterialId: string | undefined) {
  return useQuery({
    queryKey: [MOVEMENTS_KEY, "raw_material", rawMaterialId],
    queryFn: () => stockMovementService.getRawMaterialMovements(rawMaterialId!),
    enabled: !!rawMaterialId,
  });
}

/**
 * Fetch recent movements across all ingredients
 */
export function useRecentMovements(limit = 20) {
  return useQuery({
    queryKey: [MOVEMENTS_KEY, "recent", limit],
    queryFn: () => stockMovementService.getRecentMovements(limit),
  });
}

/**
 * Create a manual stock adjustment
 */
export function useCreateStockAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StockAdjustmentFormData) =>
      stockMovementService.createAdjustment(data),
    onSuccess: (_, variables) => {
      // Invalidate movements for the specific ingredient
      queryClient.invalidateQueries({
        queryKey: [MOVEMENTS_KEY, variables.ingredient_type],
      });
      // Invalidate recent movements
      queryClient.invalidateQueries({
        queryKey: [MOVEMENTS_KEY, "recent"],
      });
      // Invalidate stock queries since stock was updated
      if (variables.ingredient_type === "oil") {
        queryClient.invalidateQueries({ queryKey: [OIL_TYPES_KEY] });
      } else {
        queryClient.invalidateQueries({ queryKey: [RAW_MATERIALS_KEY] });
      }
    },
  });
}
